import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { SwitchField, MoneyField, Text, ContentLoader } from '@frontend/design-system';
import { useAppointmentTypePaymentFields } from './use-appointment-type-payment-fields';

interface AppointmentTypePaymentFieldsProps {
  getFieldProps: ReturnType<typeof useAppointmentTypePaymentFields>['getFieldProps'];
  appointmentTypeLocationId?: string; // to disable global settings fields based on appointment type location

  requireDepositSwitchLabel?: string;
}

export const AppointmentTypePaymentFields = ({
  appointmentTypeLocationId = '',
  getFieldProps,
  requireDepositSwitchLabel,
}: AppointmentTypePaymentFieldsProps) => {
  const { t } = useTranslation('schedule');
  const requireDepositField = getFieldProps('requireDeposit');
  const listLocationSettingsQuery = SchedulerV3.Queries.useListLocationSettingsQuery({
    request: {
      locationIds: [appointmentTypeLocationId],
    },
    options: {
      enabled: !!appointmentTypeLocationId,
    },
  });

  const globalDepositAmount =
    listLocationSettingsQuery.data?.locationsSettings?.[appointmentTypeLocationId]?.bookingDeposit ?? 0;
  const hasGlobalDepositAmount = !!globalDepositAmount && globalDepositAmount > 0;

  return (
    <section css={[containerStyle, appointmentTypeLocationId && { margin: theme.spacing(2, 0) }]}>
      <ContentLoader show={listLocationSettingsQuery.isLoading} size='small' />
      <SwitchField
        label={requireDepositSwitchLabel || t('Request below amount for the appointment type')}
        {...requireDepositField}
        disabled={hasGlobalDepositAmount}
      />
      {hasGlobalDepositAmount && (
        <Text color='light' size='small' css={{ marginTop: theme.spacing(1) }}>
          <Trans t={t} values={{ globalDepositAmount }}>
            <strong>Note:</strong> The system is currently set to request a payment deposit of
            <strong> ${{ globalDepositAmount }}</strong>, as specified in the Appointment Type Payments Settings. If you
            wish to customize the payment deposit for this specific appointment type, kindly ensure that you disable the
            global Appointment Type Payments Settings on the appointment type page.
          </Trans>
        </Text>
      )}
      <MoneyField
        disabled={!requireDepositField.value || hasGlobalDepositAmount}
        label={t('Amount')}
        helperText={t('Whole dollar amounts only')}
        {...getFieldProps('amount')}
        css={{ maxWidth: 200, marginTop: theme.spacing(2) }}
      />
    </section>
  );
};

const containerStyle = css({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
});
